/**
 * conectividade — o estado da rede da PESSOA, legível de qualquer lugar.
 *
 * Duas coisas, e só duas:
 *  - `online`: o que o aparelho diz (navigator.onLine + eventos online/offline).
 *  - `instavel`: o aparelho diz que está online, mas as chamadas estão
 *    falhando no transporte (DNS, timeout, reset) — Wi-Fi fraco, 3G ruim,
 *    portal cativo. `navigator.onLine` fica `true` em todos esses casos, e é
 *    exatamente aí que a pessoa culpa o app.
 *
 * Quem alimenta `instavel` é a sonda de fetch em errosDoApp.js. Quem consome
 * é o ConectividadeHost (o modal). Nada aqui faz requisição: medir a rede com
 * pings custaria rede e bateria a toda a base para achar um problema que já
 * aparece sozinho na primeira chamada que falha.
 */

import {
  avaliarFalhas,
  conexaoLenta,
  ACALMAR_MS,
} from './regras.js';

const estado = { online: true, instavel: false };
const ouvintes = new Set();
let falhas = [];
let acalmar = null;
let instalado = false;

function notificar() {
  const foto = lerEstado();
  ouvintes.forEach((fn) => {
    try { fn(foto); } catch { /* um ouvinte quebrado não derruba os outros */ }
  });
}

export function lerEstado() {
  return { ...estado };
}

/** Assina mudanças. Devolve a função de cancelar. */
export function assinar(fn) {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

function definir(parcial) {
  let mudou = false;
  for (const [k, v] of Object.entries(parcial)) {
    if (estado[k] !== v) { estado[k] = v; mudou = true; }
  }
  if (mudou) notificar();
}

function agendarAcalmar() {
  clearTimeout(acalmar);
  acalmar = setTimeout(() => {
    falhas = [];
    definir({ instavel: false });
  }, ACALMAR_MS);
}

/** Uma falha de transporte (não um 4xx/5xx: esses são resposta, não rede). */
export function registrarFalhaDeTransporte(agora = Date.now()) {
  if (!estado.online) return; // sem rede não é "instável", é "sem rede"
  const { recentes, instavel } = avaliarFalhas([...falhas, agora], agora);
  falhas = recentes;
  if (instavel) {
    definir({ instavel: true });
    agendarAcalmar();
  }
}

/** Uma resposta chegou (qualquer status): o transporte está de pé. */
export function registrarSucesso() {
  if (!falhas.length && !estado.instavel) return;
  falhas = [];
  clearTimeout(acalmar);
  definir({ instavel: false });
}

function aoMudarOnline() {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  if (online) {
    definir({ online: true });
  } else {
    falhas = [];
    clearTimeout(acalmar);
    definir({ online: false, instavel: false });
  }
}

function aoMudarConexao() {
  const tipo = navigator?.connection?.effectiveType;
  // O tipo lento sozinho NÃO abre o modal: só conta como uma falha a mais.
  // Se as chamadas continuam respondendo, a estimativa do navegador estava
  // errada e a pessoa não precisa saber dela.
  if (conexaoLenta(tipo)) registrarFalhaDeTransporte();
}

/** Liga os listeners uma vez. Devolve a função que desliga. */
export function instalar() {
  if (instalado || typeof window === 'undefined') return () => {};
  instalado = true;
  aoMudarOnline();
  window.addEventListener('online', aoMudarOnline);
  window.addEventListener('offline', aoMudarOnline);
  const conexao = navigator?.connection;
  conexao?.addEventListener?.('change', aoMudarConexao);
  aoMudarConexao();
  return () => {
    window.removeEventListener('online', aoMudarOnline);
    window.removeEventListener('offline', aoMudarOnline);
    conexao?.removeEventListener?.('change', aoMudarConexao);
    clearTimeout(acalmar);
    instalado = false;
  };
}

/** Só para teste. */
export function _zerar() {
  falhas = [];
  clearTimeout(acalmar);
  estado.online = true;
  estado.instavel = false;
}
